import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useRegistration } from './useRegistration'

const TOKEN_PARAM_NAMES = ['token', 'at'] as const
const VERIFICATION_PARAM_NAME = 'verification'

const validateTokenFormat = (token: string): boolean =>
  Boolean(token && token.length > 10 && /^[a-zA-Z0-9\-_]+$/.test(token))

const extractTokensFromParams = (searchParams: URLSearchParams) => {
  const tokens: string[] = []
  TOKEN_PARAM_NAMES.forEach((paramName) => {
    const token = searchParams.get(paramName)
    if (token && validateTokenFormat(token)) {
      tokens.push(token)
    }
  })
  return tokens
}

export const useEmailVerification = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { updateField, gotoStepByName, setRegistrationToken, loadRegistrationContext } = useRegistration()

  const urlParams = React.useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      params,
      tokens: extractTokensFromParams(params),
      hasVerification: params.has(VERIFICATION_PARAM_NAME),
      allParams: Object.fromEntries(params.entries()),
    }
  }, [location.search])

  const isEmailVerificationCallback = React.useMemo(() => {
    const { tokens, hasVerification } = urlParams
    return tokens.length > 0 && hasVerification
  }, [urlParams, location.pathname])

  const handleEmailVerificationCallback = React.useCallback(async () => {
    if (!isEmailVerificationCallback) return

    const { tokens } = urlParams
    const token = tokens[0]

    try {
      const registrationContext = loadRegistrationContext()
      if (registrationContext) {
        await updateField('userName', registrationContext.userName)
        await updateField('stateToken', registrationContext.stateToken)
        await updateField('isActivationFlow', true)
        await updateField('activationToken', token)
        setRegistrationToken(registrationContext.registrationToken)
      } else {
        await updateField('activationToken', token)
        await updateField('isActivationFlow', true)
        setRegistrationToken(token)
      }

      gotoStepByName('setPassword' as any)
      const cleanUrl = location.pathname
      navigate(cleanUrl, { replace: true })
    } catch (error) {}
  }, [isEmailVerificationCallback, urlParams, location.pathname, navigate, updateField, gotoStepByName, loadRegistrationContext, setRegistrationToken])

  React.useEffect(() => {
    if (isEmailVerificationCallback) {
      handleEmailVerificationCallback()
    }
  }, [isEmailVerificationCallback, handleEmailVerificationCallback])

  const hasValidToken = React.useMemo(() => urlParams.tokens.length > 0, [urlParams.tokens])

  return {
    isEmailVerificationCallback,
    handleEmailVerificationCallback,
    hasValidToken,
    getValidTokens: () => urlParams.tokens,
    hasVerificationParam: () => urlParams.hasVerification,
  }
}
